import React, { useState, useEffect, useRef } from 'react';
import './Pagamento.css';
import { BiSearch, BiUser, BiMap, BiPackage, BiCreditCard, BiLoader, BiBarcode, BiShow, BiX, BiPrinter, BiCheckCircle } from "react-icons/bi";
import Logo from './Logo.png';
import PagamentoOK from './pagamentook.png';
import { QRCodeSVG } from 'qrcode.react';

// Componente para gerar QR Code
const QRCodeGenerator = ({ text, size = 200 }) => {
  if (!text) return null;

  return (
    <div className="qrcode-container">
      <QRCodeSVG 
        value={text}
        size={size}
        level="M"
        includeMargin={true}
        className="qrcode-svg"
      />
      <div className="qrcode-label">Código PIX</div>
    </div>
  );
};

const Pagamento = ({ user }) => {
  const [cpfCnpj, setCpfCnpj] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [carregandoFatura, setCarregandoFatura] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [fatura, setFatura] = useState(null);
  const [mostrarQRCode, setMostrarQRCode] = useState(false);
  const [erro, setErro] = useState('');
  const [credenciais, setCredenciais] = useState(null);
  const [buscaVisivel, setBuscaVisivel] = useState(true);
  const [mostrarTeclado, setMostrarTeclado] = useState(true);
  
  // Novos estados para o contador e verificação de pagamento
  const [tempoRestante, setTempoRestante] = useState(300);
  const [pagamentoConfirmado, setPagamentoConfirmado] = useState(false);
  const [verificandoPagamento, setVerificandoPagamento] = useState(false);
  
  const [imprimindoBoleto, setImprimindoBoleto] = useState(false);
  const [modalBoleto, setModalBoleto] = useState({
    aberto: false,
    url: ''
  });

  // Refs para os intervalos
  const intervaloContadorRef = useRef(null);
  const intervaloVerificacaoRef = useRef(null);

  // Carregar credenciais do .env ao iniciar
  useEffect(() => {
    carregarCredenciais();
  }, []);

  // Foca no input quando o teclado estiver visível
  useEffect(() => {
    if (mostrarTeclado && buscaVisivel) {
      const input = document.getElementById('cpfCnpj');
      if (input) {
        input.focus();
      }
    }
  }, [mostrarTeclado, buscaVisivel]);

  // Efeito para gerenciar o contador e verificação de pagamento
  useEffect(() => {
    if (mostrarQRCode && fatura && !pagamentoConfirmado) {
      // Iniciar contador regressivo
      intervaloContadorRef.current = setInterval(() => {
        setTempoRestante((prevTempo) => {
          if (prevTempo <= 1) {
            // ⏰ TEMPO ESGOTADO - Oculta o QR Code
            clearInterval(intervaloContadorRef.current);
            clearInterval(intervaloVerificacaoRef.current);
            setMostrarQRCode(false);
            setTempoRestante(300);
            return 0;
          }
          return prevTempo - 1;
        });
      }, 1000);

      // Iniciar verificação a cada 10 segundos
      intervaloVerificacaoRef.current = setInterval(() => {
        verificarPagamento();
      }, 10000);

      // Limpar intervalos quando o componente for desmontado ou quando o pagamento for confirmado
      return () => {
        if (intervaloContadorRef.current) {
          clearInterval(intervaloContadorRef.current);
        }
        if (intervaloVerificacaoRef.current) {
          clearInterval(intervaloVerificacaoRef.current);
        }
      };
    }
  }, [mostrarQRCode, fatura, pagamentoConfirmado]);

  // Efeito para redirecionar após confirmação do pagamento
  useEffect(() => {
    if (pagamentoConfirmado) {
      const timer = setTimeout(() => {
        voltarParaInicio();
      }, 10000);

      return () => clearTimeout(timer);
    }
  }, [pagamentoConfirmado]);

  const carregarCredenciais = async () => {
    try {
      const response = await fetch('http://localhost:5000/api/variaveis-ambiente');
      if (response.ok) {
        const data = await response.json();
        setCredenciais(data.integracoes);
      }
    } catch (error) {
      console.error('Erro ao carregar credenciais:', error);
    }
  };

  // ✅ FUNÇÃO PARA VERIFICAR PAGAMENTO
  const verificarPagamento = async () => {
    if (!fatura || !credenciais || verificandoPagamento || pagamentoConfirmado) return;

    setVerificandoPagamento(true);

    try {
      const payload = {
        cpfcnpj: cpfCnpj.replace(/\D/g, ''),
        contrato: fatura.contrato,
        senha: fatura.senha
      };

      const response = await fetch(`${credenciais.url}/api/central/fatura2via`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        
        // Se não encontrar fatura, significa que foi paga
        if (data.status === 0 || data.msg?.includes('não encontrada') || data.msg?.includes('não existe')) {
          setPagamentoConfirmado(true);
          // Limpar intervalos
          if (intervaloContadorRef.current) {
            clearInterval(intervaloContadorRef.current);
          }
          if (intervaloVerificacaoRef.current) {
            clearInterval(intervaloVerificacaoRef.current);
          }
        }
      }
    } catch (error) {
      console.error('Erro ao verificar pagamento:', error);
    } finally {
      setVerificandoPagamento(false);
    }
  };

  // ✅ FUNÇÃO PARA VOLTAR AO INÍCIO
  const voltarParaInicio = () => {
    setPagamentoConfirmado(false);
    setMostrarQRCode(false);
    setFatura(null);
    setResultado(null);
    setBuscaVisivel(true);
    setMostrarTeclado(true);
    setTempoRestante(300);
    setCpfCnpj('');
    
    if (intervaloContadorRef.current) {
      clearInterval(intervaloContadorRef.current);
    }
    if (intervaloVerificacaoRef.current) {
      clearInterval(intervaloVerificacaoRef.current);
    }
    
    setTimeout(() => {
      const input = document.getElementById('cpfCnpj');
      if (input) {
        input.focus();
      }
    }, 100);
  };

  // ✅ FUNÇÃO PARA FORMATAR O TEMPO (MM:SS)
  const formatarTempo = (segundos) => {
    const minutos = Math.floor(segundos / 60);
    const segundosRestantes = segundos % 60;
    return `${minutos.toString().padStart(2, '0')}:${segundosRestantes.toString().padStart(2, '0')}`;
  };

  // ✅ FUNÇÃO PARA IMPRIMIR BOLETO
  const imprimirBoleto = async (urlBoleto) => {
    setImprimindoBoleto(true);
    setErro('');
    
    try {
      const response = await fetch('http://localhost:5000/api/imprimir-boleto', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          boletoUrl: urlBoleto,
          impressora: ""
        })
      });
      
      const resultado = await response.json();
      
      if (response.ok && resultado.success) {
        alert('✅ Boleto enviado para impressão com sucesso!');
      } else {
        throw new Error(resultado.message || 'Erro na impressão');
      }
      
    } catch (error) {
      console.error('Erro ao imprimir:', error);
      setErro('Erro ao imprimir boleto: ' + error.message);
      
      setModalBoleto({
        aberto: true,
        url: urlBoleto
      });
    } finally {
      setImprimindoBoleto(false);
    }
  };

  const adicionarDigito = (digito) => {
    const currentValue = cpfCnpj.replace(/\D/g, '');
    let newValue = currentValue + digito;
    
    if (newValue.length <= 14) {
      setCpfCnpj(formatarCpfCnpj(newValue));
    }
    
    const input = document.getElementById('cpfCnpj');
    if (input) {
      input.focus();
    }
  };

  const removerDigito = () => {
    const currentValue = cpfCnpj.replace(/\D/g, '');
    const newValue = currentValue.slice(0, -1);
    setCpfCnpj(formatarCpfCnpj(newValue));
    
    const input = document.getElementById('cpfCnpj');
    if (input) {
      input.focus();
    }
  };

  const limparCampo = () => {
    setCpfCnpj('');
    setMostrarTeclado(true);
    
    const input = document.getElementById('cpfCnpj');
    if (input) {
      input.focus();
    }
  };

  const formatarCpfCnpj = (value) => {
    const numbers = value.replace(/\D/g, '');
    
    if (numbers.length <= 11) {
      return numbers
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    } else {
      return numbers
        .replace(/(\d{2})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1/$2')
        .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
    }
  };

  const handleCpfCnpjChange = (e) => {
    const formatted = formatarCpfCnpj(e.target.value);
    setCpfCnpj(formatted);
    setErro('');
    setResultado(null);
    setFatura(null);
    setMostrarQRCode(false);
    setPagamentoConfirmado(false);
    setTempoRestante(300);
  };

  const validarCpfCnpj = (value) => {
    const numbers = value.replace(/\D/g, '');
    return numbers.length === 11 || numbers.length === 14;
  };

  const buscarContratos = async () => {
    if (!cpfCnpj) {
      setErro('Por favor, informe um CPF ou CNPJ');
      return;
    }

    if (!validarCpfCnpj(cpfCnpj)) {
      setErro('CPF/CNPJ inválido');
      return;
    }

    if (!credenciais || !credenciais.token || !credenciais.app || !credenciais.url) {
      setErro('Credenciais não configuradas. Verifique as configurações do sistema.');
      return;
    }

    setCarregando(true);
    setErro('');
    setResultado(null);
    setFatura(null);
    setMostrarQRCode(false);
    setPagamentoConfirmado(false);
    setTempoRestante(300);
    setMostrarTeclado(false);

    try {
      const payload = {
        cpfcnpj: cpfCnpj.replace(/\D/g, ''),
        token: credenciais.token,
        app: credenciais.app
      };

      const response = await fetch(`${credenciais.url}/ws/ura/consultacliente`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`Erro na requisição: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.msg && data.contratos && data.contratos.length > 0) {
        setResultado(data);
        setBuscaVisivel(false);
      } else {
        setErro(data.msg || 'Nenhum contrato encontrado para este CPF/CNPJ');
      }

    } catch (error) {
      console.error('Erro na busca:', error);
      setErro('Erro ao buscar contratos. Verifique a conexão e as credenciais.');
    } finally {
      setCarregando(false);
    }
  };

  const buscarFatura = async (contrato) => {
    if (!credenciais || !contrato.contratoCentralSenha) {
      setErro('Senha do contrato não disponível');
      alert('Erro: SenhaCentral - Solicite ajuda a uma atendente.')
      return;
    }

    setCarregandoFatura(contrato.contratoId);
    setFatura(null);
    setMostrarQRCode(false);
    setPagamentoConfirmado(false);
    setTempoRestante(300);

    try {
      const payload = {
        cpfcnpj: cpfCnpj.replace(/\D/g, ''),
        contrato: contrato.contratoId,
        senha: contrato.contratoCentralSenha
      };

      const response = await fetch(`${credenciais.url}/api/central/fatura2via`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`Erro na requisição: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.status === 1) {
        data.senha = contrato.contratoCentralSenha;
        setFatura(data);
        setResultado(null);
      } else {
        setErro(data.msg || 'Erro ao buscar fatura');
        console.error('Não há faturas em aberto para o contrato selecionado.');
        alert('Não há faturas pendentes para o contrato selecionado.')
      }

    } catch (error) {
      console.error('Erro ao buscar fatura:', error);
      setErro('Erro ao buscar fatura. Tente novamente.');
    } finally {
      setCarregandoFatura(null);
    }
  };

  const voltarParaBusca = () => {
    setBuscaVisivel(true);
    setResultado(null);
    setFatura(null);
    setMostrarQRCode(false);
    setPagamentoConfirmado(false);
    setTempoRestante(300);
    setMostrarTeclado(true);
    
    setTimeout(() => {
      const input = document.getElementById('cpfCnpj');
      if (input) {
        input.focus();
      }
    }, 100);
  };

  const voltarParaContratos = () => {
    setResultado(resultado);
    setFatura(null);
    setMostrarQRCode(false);
    setPagamentoConfirmado(false);
    setTempoRestante(300);
    setBuscaVisivel(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    buscarContratos();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      buscarContratos();
    }
  };

  const handleInputClick = () => {
    setMostrarTeclado(true);
  };

  const formatarValor = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor || 0);
  };

  const formatarData = (data) => {
    if (!data) return 'N/A';
    return new Date(data).toLocaleDateString('pt-BR');
  };

  const toggleQRCode = () => {
    if (mostrarQRCode) {
      setMostrarQRCode(false);
      if (intervaloContadorRef.current) {
        clearInterval(intervaloContadorRef.current);
      }
      if (intervaloVerificacaoRef.current) {
        clearInterval(intervaloVerificacaoRef.current);
      }
    } else {
      setMostrarQRCode(true);
      setTempoRestante(300);
      setPagamentoConfirmado(false);
    }
  };

  return (
    <div className="pagamento-container">
      {buscaVisivel && (
        <div className="busca-centralizada">
          <div className="busca-card">
            <div className="logo-container">
              <div className="logo">
                <img 
                  src={Logo} 
                  alt="Logo" 
                  className="logo-img"
                />
              </div>
            </div>
            
            <form onSubmit={handleSubmit} className="busca-form">
              <div className="form-group">
                <label htmlFor="cpfCnpj" className="form-label">
                  Digite o CPF ou CNPJ
                </label>
                <div className="input-with-button">
                  <input
                    type="text"
                    id="cpfCnpj"
                    value={cpfCnpj}
                    onChange={handleCpfCnpjChange}
                    onKeyPress={handleKeyPress}
                    onClick={handleInputClick}
                    className={`busca-input ${erro ? 'error' : ''}`}
                    placeholder=""
                    disabled={carregando}
                    autoFocus
                    readOnly
                  />
                  <button 
                    type="submit" 
                    className="busca-button"
                    disabled={carregando || !cpfCnpj}
                  >
                    {carregando ? <BiLoader className="spinner" /> : <BiSearch />}
                    {carregando ? 'Buscando...' : 'Buscar'}
                  </button>
                </div>
                {erro && <span className="error-message">{erro}</span>}
              </div>
            </form>

            {mostrarTeclado && buscaVisivel && (
              <div className="teclado-virtual">
                <div className="teclado-header">
                  <span>Teclado Virtual</span>
                  <button 
                    className="teclado-fechar"
                    onClick={() => setMostrarTeclado(false)}
                    title="Fechar teclado"
                  >
                    <BiX />
                  </button>
                </div>
                
                <div className="teclado-numerico">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 0].map((numero) => (
                    <button
                      key={numero}
                      className="tecla-numero"
                      onClick={() => adicionarDigito(numero.toString())}
                    >
                      {numero}
                    </button>
                  ))}
                </div>
                
                <div className="teclado-acoes">
                  <button 
                    className="tecla-acao tecla-limpar"
                    onClick={limparCampo}
                  >
                    Limpar
                  </button>
                  <button 
                    className="tecla-acao tecla-apagar"
                    onClick={removerDigito}
                  >
                    ⌫ Apagar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {resultado && !fatura && (
        <div className="conteudo-centralizado">
          <div className="resultados-container">
            <div className="resultados-header">
              <h2>
                <BiUser /> Cliente: {resultado.contratos[0]?.razaoSocial || 'N/A'}
              </h2>
              <p className="resultados-info">
                {resultado.msg} • {resultado.contratos.length} contrato(s) encontrado(s)
              </p>
              <button className="btn-voltar" onClick={voltarParaBusca}>
                ← Voltar para busca
              </button>
            </div>

            <div className="contratos-grid">
              {resultado.contratos.map((contrato, index) => (
                <div 
                  key={contrato.contratoId || index} 
                  className={`contrato-card ${carregandoFatura === contrato.contratoId ? 'loading' : ''}`}
                  onClick={() => buscarFatura(contrato)}
                >
                  {carregandoFatura === contrato.contratoId && (
                    <div className="loading-overlay">
                      <BiLoader className="spinner" />
                      <span>Buscando fatura...</span>
                    </div>
                  )}
                  
                  <div className="contrato-info">
                    <div className="info-line">
                      <BiCreditCard className="info-icon" />
                      <strong>Contrato:</strong> {contrato.contratoId || 'N/A'}
                    </div>
                    
                    <div className="info-line">
                      <BiMap className="info-icon" />
                      <strong>Endereço:</strong> 
                      <span>
                        {contrato.endereco_logradouro || 'Endereço não informado'}, 
                        {contrato.endereco_numero || ' S/N'}
                        {contrato.endereco_complemento ? ` - ${contrato.endereco_complemento}` : ''}
                      </span>
                    </div>
                    
                    <div className="info-line">
                      <BiPackage className="info-icon" />
                      <strong>Pacote:</strong> {contrato.servico_plano || 'N/A'}
                    </div>
                  </div>
                  
                  <div className="contrato-action">
                    <span>Clique para ver fatura</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {fatura && (
        <div className="conteudo-centralizado">
          <div className="fatura-container">
            <div className="fatura-header">
              <button className="btn-voltar" onClick={voltarParaContratos}>
                ← Voltar para contratos
              </button>
              <h2>
                <BiCreditCard /> Fatura - Contrato {fatura.contrato}
              </h2>
            </div>

            <div className="fatura-card">
              <div className="fatura-info">
                <div className="fatura-detalhes">
                  <div className="detalhe-item">
                    <strong>Cliente:</strong>
                    <span>{fatura.razaosocial}</span>
                  </div>
                  <div className="detalhe-item">
                    <strong>CPF/CNPJ:</strong>
                    <span>{fatura.cpfcnpj}</span>
                  </div>
                  <div className="detalhe-item">
                    <strong>Vencimento:</strong>
                    <span>{formatarData(fatura.vencimento)}</span>
                  </div>
                  <div className="detalhe-item">
                    <strong>Valor:</strong>
                    <span className="valor-destaque">{formatarValor(fatura.valor)}</span>
                  </div>
                  <div className="detalhe-item">
                    <strong>Protocolo:</strong>
                    <span>{fatura.protocolo}</span>
                  </div>
                </div>

                <div className="fatura-opcoes">
                  {fatura.linhadigitavel && (
                    <div className="opcao-pagamento">
                      <BiBarcode className="opcao-icon" />
                      <div className="opcao-content">
                        <strong>Boleto Bancário</strong>
                        <span>Linha digitável: {fatura.linhadigitavel}</span>
                        <button 
                          onClick={() => imprimirBoleto(fatura.link)}
                          disabled={imprimindoBoleto}
                          className="btn-secundario"
                        >
                          {imprimindoBoleto ? (
                            <>
                              <BiLoader className="spinner" />
                              Imprimindo...
                            </>
                          ) : (
                            <>
                              <BiPrinter />
                              Imprimir Boleto
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  {fatura.codigopix && (
                    <div className="opcao-pagamento">
                      <BiShow className="opcao-icon" />
                      <div className="opcao-content">
                        <strong>PIX</strong>
                        <span>Código PIX disponível</span>
                        
                        {!mostrarQRCode && tempoRestante === 0 && (
                          <div className="tempo-esgotado-aviso">
                            <BiX className="icone-esgotado" />
                            <span>Tempo para pagamento esgotado. Clique em "Mostrar QR Code" para gerar novamente.</span>
                          </div>
                        )}
                        
                        <button 
                          className="btn-primario"
                          onClick={toggleQRCode}
                        >
                          {mostrarQRCode ? 'Ocultar QR Code' : 'Mostrar QR Code'}
                        </button>
                        
                        {mostrarQRCode && (
                          <div className="qrcode-section">
                            {!pagamentoConfirmado && (
                              <div className={`contador-regressivo ${tempoRestante <= 60 ? 'critico' : ''}`}>
                                <div className="contador-tempo">
                                  {formatarTempo(tempoRestante)}
                                </div>
                                <div className="contador-label">
                                  {tempoRestante <= 60 ? 'Tempo está acabando!' : 'Tempo restante para pagamento'}
                                </div>
                              </div>
                            )}
                            
                            {pagamentoConfirmado ? (
                              <div className="confirmacao-pagamento">
                                <img 
                                  src={PagamentoOK} 
                                  alt="Pagamento Confirmado" 
                                  className="imagem-confirmacao"
                                />
                                <div className="texto-confirmacao">
                                  <BiCheckCircle className="icone-confirmacao" />
                                  Pagamento confirmado!
                                </div>
                              </div>
                            ) : (
                              <QRCodeGenerator text={fatura.codigopix} size={200} />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {modalBoleto.aberto && (
        <div className="modal-overlay">
          <div className="modal-boleto">
            <div className="modal-header">
              <h3><BiPrinter /> Boleto Bancário</h3>
              <button 
                className="modal-fechar"
                onClick={() => setModalBoleto({ aberto: false, url: '' })}
              >
                <BiX />
              </button>
            </div>
            
            <div className="modal-content">
              <div className="boleto-container">
                <iframe
                  src={modalBoleto.url}
                  className="boleto-iframe"
                  title="Boleto Bancário"
                />
              </div>
              
              <div className="modal-acoes">
                <button 
                  className="btn-primario"
                  onClick={() => window.print()}
                >
                  <BiPrinter /> Imprimir Boleto
                </button>
                
                <button 
                  className="btn-secundario"
                  onClick={() => setModalBoleto({ aberto: false, url: '' })}
                >
                  Fechar
                </button>
              </div>
              
              <div className="instrucoes-impressao">
                <p><strong>Instruções de Impressão:</strong></p>
                <ul>
                  <li>Clique em "Imprimir Boleto" acima</li>
                  <li>Selecione sua impressora nas configurações</li>
                  <li>Clique em "Imprimir"</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {!credenciais?.token && (
        <div className="aviso-credenciais">
          <p>
            ⚠️ As credenciais da API não estão configuradas. 
            <a href="/configuracoes" className="link-config"> Configure-as aqui</a>.
          </p>
        </div>
      )}
    </div>
  );
};

export default Pagamento;